import React, { useCallback, useState } from "react";
import { Flex, FileUpload, ErrorMessage } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import useUploadFilesToS3 from "../../../../common/hooks/uploadFilesToS3/useUploadFilesToS3";
import { PayableDocument } from "../../types";
import { fullWidthFileUploadStyles } from "./styles_vanilla.css";
export type PayableDocumentUploadProps = {
  /**
   * Handler called after files are successfully uploaded
   */
  onFilesUploaded: (files: PayableDocument[]) => void;

  /**
   * Handler called when files are deleted
   */
  onFilesDeleted?: () => void;

  /**
   * Error message to display
   */
  error?: string;
};

// Re-export PayableDocument for convenience
export type PayableUploadedDocument = PayableDocument;

type FileObject = {
  status: string;
  file: File;
  uuid?: string;
};

export const PayableDocumentUpload: React.FC<PayableDocumentUploadProps> = ({
  onFilesUploaded,
  onFilesDeleted,
  error,
}) => {
  const { uploadFilesToS3, loading } = useUploadFilesToS3();
  const [uploadError, setUploadError] = useState<string>("");

  const handleFileSelected = useCallback(
    (fileObjs: FileObject[]) => {
      const acceptedFiles = fileObjs
        .filter((obj) => obj.status === "accepted")
        .map((obj) => obj.file);

      if (acceptedFiles.length === 0) return;

      setUploadError("");

      // Handle upload asynchronously but don't return the promise
      uploadFilesToS3(acceptedFiles)
        .then((uploadResults) => {
          // Convert upload results to PayableDocument format
          const uploadedDocuments: PayableDocument[] = uploadResults.map((result) => ({
            name: result.file.name,
            size: result.file.size,
            s3ObjectKey: result.objectKey,
            localUrl: URL.createObjectURL(result.file),
            file: result.file,
            contentType: result.file.type,
          }));

          onFilesUploaded(uploadedDocuments);
        })
        .catch((e: unknown) => {
          addError(formatError(e));
          setUploadError("Upload failed. Please try again.");
        });
    },
    [uploadFilesToS3, onFilesUploaded],
  );

  const displayError = error || uploadError;

  return (
    <Flex flexDirection="column" gap={16} data-testid="payable-document-upload">
      <FileUpload
        uploadMessage="Drop the files here or"
        acceptedFileTypes={[".jpg", ".png", ".pdf"]}
        canAllowMultiple={false}
        displayAcceptedFileTypes={true}
        alwaysShowGuidance={true}
        onFileSelected={handleFileSelected}
        size="large"
        className={fullWidthFileUploadStyles}
        showFileList={true}
        data-cy="payable-file-upload"
        onDelete={onFilesDeleted}
        data-testid="file-upload-component"
        isDisabled={loading}
      />

      {displayError && <ErrorMessage>{displayError}</ErrorMessage>}
    </Flex>
  );
};

export default PayableDocumentUpload;
